import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useLanguage } from '../context/LanguageContext';
import './AdminGamesManager.css';

const weiToUsd = (wei) => {
  if (!wei || wei === '0') return '0.00';
  const n = Number(BigInt(wei)) / 1e18;
  return n.toFixed(2);
};

const usdToWei = (usd) => {
  const s = String(usd || '').trim();
  if (!s) return '0';
  const [whole = '0', frac = ''] = s.split('.');
  const padded = (frac + '000000000000000000').slice(0, 18);
  const combined = BigInt(whole) * BigInt('1000000000000000000') + BigInt(padded);
  return combined.toString();
};

const AdminGamesManager = () => {
  const { t } = useLanguage();
  const [subTab, setSubTab] = useState('draws');

  return (
    <div className="admin-games-manager">
      <div className="admin-games-subtabs">
        <button className={`agm-subtab ${subTab === 'draws' ? 'agm-subtab-active' : ''}`} onClick={() => setSubTab('draws')}>
          {t('luckyDraws')}
        </button>
        <button className={`agm-subtab ${subTab === 'predictions' ? 'agm-subtab-active' : ''}`} onClick={() => setSubTab('predictions')}>
          {t('predictions')}
        </button>
      </div>

      {subTab === 'draws' && <AdminDrawsPanel />}
      {subTab === 'predictions' && <AdminPredictionsPanel />}
    </div>
  );
};

// ─── Lucky Draws Admin ──────────────────────────────────────────────

const PRIZE_TYPES = ['pool', 'nft', 'smmDiscount'];

const AdminDrawsPanel = () => {
  const { t } = useLanguage();
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', entryFeeUsd: '', maxEntries: '0', maxEntriesPerUser: '1',
    startsAt: '', endsAt: '', prizes: [{ type: 'pool', label: '', value: '', quantity: '1' }]
  });

  const fetchDraws = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/lucky-draw/draws');
      setDraws(res.data?.draws || []);
    } catch (e) {
      toast.error(t('failedToLoadDraws'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchDraws(); }, [fetchDraws]);

  const addPrize = () => {
    setForm(f => ({ ...f, prizes: [...f.prizes, { type: 'pool', label: '', value: '', quantity: '1' }] }));
  };

  const removePrize = (idx) => {
    setForm(f => ({ ...f, prizes: f.prizes.filter((_, i) => i !== idx) }));
  };

  const updatePrize = (idx, field, val) => {
    setForm(f => {
      const p = [...f.prizes];
      p[idx] = { ...p[idx], [field]: val };
      return { ...f, prizes: p };
    });
  };

  const handleCreate = async () => {
    try {
      const feeUsd = parseFloat(form.entryFeeUsd);
      if (!Number.isFinite(feeUsd) || feeUsd <= 0) {
        return toast.error(t('invalidEntryFee'));
      }
      await api.post('/lucky-draw/admin/draws', {
        title: form.title,
        description: form.description,
        entryFeeWei: usdToWei(form.entryFeeUsd),
        entryFeeUsd: feeUsd,
        maxEntries: Number(form.maxEntries) || 0,
        maxEntriesPerUser: Number(form.maxEntriesPerUser) || 1,
        prizes: form.prizes.map(p => ({
          type: p.type,
          label: p.label,
          value: p.type === 'pool' ? Number(p.value) : p.type === 'smmDiscount' ? Number(p.value) : p.value,
          quantity: Number(p.quantity) || 1
        })),
        startsAt: form.startsAt,
        endsAt: form.endsAt
      });
      toast.success(t('drawCreated'));
      setShowForm(false);
      fetchDraws();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('failedToCreateDraw'));
    }
  };

  const handleExecute = async (id) => {
    if (!window.confirm(t('confirmExecuteDraw'))) return;
    try {
      const res = await api.post(`/lucky-draw/admin/draws/${id}/execute`);
      toast.success(res.data?.message || t('drawExecuted'));
      fetchDraws();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('failedToExecuteDraw'));
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(t('confirmCancelDraw'))) return;
    try {
      const res = await api.post(`/lucky-draw/admin/draws/${id}/cancel`);
      toast.success(res.data?.message || t('drawCancelled'));
      fetchDraws();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('failedToCancelDraw'));
    }
  };

  if (loading) return <div className="agm-loading">{t('loading')}</div>;

  return (
    <div className="agm-panel">
      <div className="agm-panel-header">
        <h3>{t('luckyDraws')}</h3>
        <button className="adm-btn adm-btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? t('cancel') : `+ ${t('createDraw')}`}
        </button>
      </div>

      {showForm && (
        <div className="agm-form">
          <div className="adm-form-row">
            <label>{t('title')}</label>
            <input className="adm-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="adm-form-row">
            <label>{t('description')}</label>
            <input className="adm-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="agm-form-grid">
            <div className="adm-form-row">
              <label>{t('entryFeeUsd')}</label>
              <input className="adm-input" type="number" step="0.01" value={form.entryFeeUsd} onChange={e => setForm(f => ({ ...f, entryFeeUsd: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label>{t('maxEntries')} (0 = {t('unlimited')})</label>
              <input className="adm-input" type="number" value={form.maxEntries} onChange={e => setForm(f => ({ ...f, maxEntries: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label>{t('maxEntriesPerUser')}</label>
              <input className="adm-input" type="number" value={form.maxEntriesPerUser} onChange={e => setForm(f => ({ ...f, maxEntriesPerUser: e.target.value }))} />
            </div>
          </div>
          <div className="agm-form-grid">
            <div className="adm-form-row">
              <label>{t('startsAt')}</label>
              <input className="adm-input" type="datetime-local" value={form.startsAt} onChange={e => setForm(f => ({ ...f, startsAt: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label>{t('endsAt')}</label>
              <input className="adm-input" type="datetime-local" value={form.endsAt} onChange={e => setForm(f => ({ ...f, endsAt: e.target.value }))} />
            </div>
          </div>

          <div className="agm-prizes-section">
            <label>{t('prizes')}</label>
            {form.prizes.map((p, i) => (
              <div key={i} className="agm-prize-row">
                <select className="adm-input" value={p.type} onChange={e => updatePrize(i, 'type', e.target.value)}>
                  {PRIZE_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                </select>
                <input className="adm-input" placeholder={t('prizeLabel')} value={p.label} onChange={e => updatePrize(i, 'label', e.target.value)} />
                <input className="adm-input" placeholder={p.type === 'pool' ? '% of pool' : p.type === 'smmDiscount' ? t('discountPercent') : 'Token ID'} value={p.value} onChange={e => updatePrize(i, 'value', e.target.value)} />
                <input className="adm-input agm-prize-qty" type="number" min="1" placeholder={t('quantity')} value={p.quantity} onChange={e => updatePrize(i, 'quantity', e.target.value)} />
                {form.prizes.length > 1 && (
                  <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => removePrize(i)}>X</button>
                )}
              </div>
            ))}
            <button className="adm-btn adm-btn-sm" onClick={addPrize}>+ {t('addPrize')}</button>
          </div>

          <div className="adm-form-actions">
            <button className="adm-btn adm-btn-primary" onClick={handleCreate}>{t('createDraw')}</button>
          </div>
        </div>
      )}

      <div className="agm-list">
        {draws.length === 0 && <div className="agm-empty">{t('noDraws')}</div>}
        {draws.map(d => (
          <div key={d._id} className={`agm-card agm-card-${d.status}`}>
            <div className="agm-card-header">
              <h4>{d.title}</h4>
              <span className={`agm-status agm-status-${d.status}`}>{d.status}</span>
            </div>
            <div className="agm-card-details">
              <span>{t('entryFee')}: ${d.entryFeeUsd}</span>
              <span>{t('entries')}: {d.totalEntries || 0}{d.maxEntries > 0 ? ` / ${d.maxEntries}` : ''}</span>
              <span>{t('pool')}: ${weiToUsd((BigInt(d.entryFeeWei || '0') * BigInt(d.totalEntries || 0)).toString())} USDT</span>
            </div>
            <div className="agm-card-dates">
              <span>{t('start')}: {new Date(d.startsAt).toLocaleString()}</span>
              <span>{t('end')}: {new Date(d.endsAt).toLocaleString()}</span>
            </div>
            {d.prizes && d.prizes.length > 0 && (
              <div className="agm-card-prizes">
                {d.prizes.map((p, i) => (
                  <span key={i} className="agm-prize-badge">{p.label} ({p.type}: {p.value}{p.type === 'pool' || p.type === 'smmDiscount' ? '%' : ''})</span>
                ))}
              </div>
            )}
            <div className="agm-card-actions">
              {(d.status === 'active' || d.status === 'upcoming') && (
                <>
                  <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => handleExecute(d._id)}>{t('executeDraw')}</button>
                  <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => handleCancel(d._id)}>{t('cancelDraw')}</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Predictions Admin ──────────────────────────────────────────────

const AdminPredictionsPanel = () => {
  const { t } = useLanguage();
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    question: '', description: '', options: ['', ''],
    minBetUsd: '', maxBetUsd: '', platformFeePercent: '5', startsAt: '', endsAt: ''
  });

  const fetchPredictions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/predictions');
      setPredictions(res.data?.predictions || []);
    } catch (e) {
      toast.error(t('failedToLoadPredictions'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchPredictions(); }, [fetchPredictions]);

  const handleCreate = async () => {
    try {
      const filteredOptions = form.options.filter(o => o.trim());
      if (filteredOptions.length < 2) {
        return toast.error(t('atLeastTwoOptions'));
      }
      await api.post('/predictions/admin/create', {
        question: form.question,
        description: form.description,
        options: filteredOptions,
        minBetWei: form.minBetUsd ? usdToWei(form.minBetUsd) : '0',
        maxBetWei: form.maxBetUsd ? usdToWei(form.maxBetUsd) : '0',
        platformFeePercent: Number(form.platformFeePercent) || 5,
        startsAt: form.startsAt,
        endsAt: form.endsAt
      });
      toast.success(t('predictionCreated'));
      setShowForm(false);
      fetchPredictions();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('failedToCreatePrediction'));
    }
  };

  const handleResolve = async (id, optionIndex) => {
    if (!window.confirm(t('confirmResolvePrediction'))) return;
    try {
      const res = await api.post(`/predictions/admin/${id}/resolve`, { winningOptionIndex: optionIndex });
      toast.success(res.data?.message || t('predictionResolved'));
      fetchPredictions();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('failedToResolvePrediction'));
    }
  };

  const handleCancelPrediction = async (id) => {
    if (!window.confirm(t('confirmCancelPrediction'))) return;
    try {
      const res = await api.post(`/predictions/admin/${id}/cancel`);
      toast.success(res.data?.message || t('predictionCancelled'));
      fetchPredictions();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('failedToCancelPrediction'));
    }
  };

  const addOption = () => setForm(f => ({ ...f, options: [...f.options, ''] }));
  const removeOption = (idx) => setForm(f => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));
  const updateOption = (idx, val) => {
    setForm(f => {
      const opts = [...f.options];
      opts[idx] = val;
      return { ...f, options: opts };
    });
  };

  if (loading) return <div className="agm-loading">{t('loading')}</div>;

  return (
    <div className="agm-panel">
      <div className="agm-panel-header">
        <h3>{t('predictions')}</h3>
        <button className="adm-btn adm-btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? t('cancel') : `+ ${t('createPrediction')}`}
        </button>
      </div>

      {showForm && (
        <div className="agm-form">
          <div className="adm-form-row">
            <label>{t('question')}</label>
            <input className="adm-input" value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} />
          </div>
          <div className="adm-form-row">
            <label>{t('description')}</label>
            <input className="adm-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="adm-form-row">
            <label>{t('options')}</label>
            {form.options.map((o, i) => (
              <div key={i} className="agm-option-row">
                <input className="adm-input" placeholder={`${t('option')} ${i + 1}`} value={o} onChange={e => updateOption(i, e.target.value)} />
                {form.options.length > 2 && (
                  <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => removeOption(i)}>X</button>
                )}
              </div>
            ))}
            <button className="adm-btn adm-btn-sm" onClick={addOption}>+ {t('addOption')}</button>
          </div>
          <div className="agm-form-grid">
            <div className="adm-form-row">
              <label>{t('minBetUsd')} ({t('optional')})</label>
              <input className="adm-input" type="number" step="0.01" value={form.minBetUsd} onChange={e => setForm(f => ({ ...f, minBetUsd: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label>{t('maxBetUsd')} ({t('optional')})</label>
              <input className="adm-input" type="number" step="0.01" value={form.maxBetUsd} onChange={e => setForm(f => ({ ...f, maxBetUsd: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label>{t('platformFee')} %</label>
              <input className="adm-input" type="number" min="0" max="50" value={form.platformFeePercent} onChange={e => setForm(f => ({ ...f, platformFeePercent: e.target.value }))} />
            </div>
          </div>
          <div className="agm-form-grid">
            <div className="adm-form-row">
              <label>{t('startsAt')}</label>
              <input className="adm-input" type="datetime-local" value={form.startsAt} onChange={e => setForm(f => ({ ...f, startsAt: e.target.value }))} />
            </div>
            <div className="adm-form-row">
              <label>{t('endsAt')}</label>
              <input className="adm-input" type="datetime-local" value={form.endsAt} onChange={e => setForm(f => ({ ...f, endsAt: e.target.value }))} />
            </div>
          </div>
          <div className="adm-form-actions">
            <button className="adm-btn adm-btn-primary" onClick={handleCreate}>{t('createPrediction')}</button>
          </div>
        </div>
      )}

      <div className="agm-list">
        {predictions.length === 0 && <div className="agm-empty">{t('noPredictions')}</div>}
        {predictions.map(p => (
          <div key={p._id} className={`agm-card agm-card-${p.status}`}>
            <div className="agm-card-header">
              <h4>{p.question}</h4>
              <span className={`agm-status agm-status-${p.status}`}>{p.status}</span>
            </div>
            {p.description && <p className="agm-card-desc">{p.description}</p>}
            <div className="agm-card-details">
              <span>{t('pool')}: ${weiToUsd(p.totalPoolWei)} USDT</span>
              <span>{t('platformFee')}: {p.platformFeePercent}%</span>
            </div>
            <div className="agm-pred-options">
              {p.options?.map((o, i) => (
                <div key={i} className={`agm-pred-option ${p.resolvedOptionIndex === i ? 'agm-pred-winner' : ''}`}>
                  <span>{o.label}</span>
                  <span className="agm-pred-pool">${weiToUsd(o.totalBetsWei)}</span>
                  {(p.status === 'active' || p.status === 'upcoming') && (
                    <button className="adm-btn adm-btn-sm adm-btn-primary" onClick={() => handleResolve(p._id, i)}>
                      {t('resolveAsWinner')}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="agm-card-dates">
              <span>{t('start')}: {new Date(p.startsAt).toLocaleString()}</span>
              <span>{t('end')}: {new Date(p.endsAt).toLocaleString()}</span>
            </div>
            <div className="agm-card-actions">
              {p.status !== 'resolved' && p.status !== 'cancelled' && (
                <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => handleCancelPrediction(p._id)}>
                  {t('cancelPrediction')}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminGamesManager;
